import type { AnyStateMachine, EventObject, InspectionEvent, SnapshotFrom } from 'xstate';
import { z } from 'zod';

import type { PetitionDraft } from '#src/app/shared/api/petitionDraft';

import type { JobDoneEvent, JobStartEvent } from './operations/types';
import type { OrchestrationContext } from './types';

const navigationInspectionEventSchema = z
	.object({
		type: z.literal('@xstate.microstep'),
		event: z.object({ type: z.enum(['back', 'submit', 'error']) }).loose(),
		_transitions: z.array(z.object({ target: z.array(z.object({ key: z.string() })).min(1) })).min(1),
	})
	.loose();

export type NavigationInspectionEvent = Extract<InspectionEvent, { type: '@xstate.microstep' }> &
	z.infer<typeof navigationInspectionEventSchema>;

export function assertNavigationEvent(event: InspectionEvent): asserts event is NavigationInspectionEvent {
	const result = navigationInspectionEventSchema.safeParse(event);
	if (!result.success) {
		throw new Error('not a navigation event');
	}
}

/** Narrows any event to one operation's `job.start`, the way xstate's `assertEvent` narrows by type. */
export function assertJobStart<NAME extends JobStartEvent['name']>(
	event: EventObject,
	name: NAME,
): asserts event is Extract<JobStartEvent, { name: NAME }> {
	if (event.type !== 'job.start' || !('name' in event) || event.name !== name) {
		throw new Error(`expected job.start for ${name}, got ${event.type}`);
	}
}

/** Narrows any event to one operation's `job.done`, the way xstate's `assertEvent` narrows by type. */
export function assertJobDone<NAME extends JobDoneEvent['name']>(
	event: EventObject,
	name: NAME,
): asserts event is Extract<JobDoneEvent, { name: NAME }> {
	if (event.type !== 'job.done' || !('name' in event) || event.name !== name) {
		throw new Error(`expected job.done for ${name}, got ${event.type}`);
	}
}

// oxlint-disable-next-line typescript/no-explicit-any
type AnySnapshot = SnapshotFrom<AnyStateMachine>;
type RequiredSnapshot = Required<Pick<AnySnapshot, 'status' | 'value' | 'context'>> & {
	children: Record<string, { snapshot: RequiredSnapshot; src: string }>;
};

// TODO: update this from `metadata.variant` to flow property and remove mapping
const FLOW: Record<string, string> = {
	control: 'control',
	fake_control: 'control',
};
// oxlint-disable-next-line typescript/no-explicit-any
export function buildSnapshot(draft: OrchestrationContext['draft'] | PetitionDraft, step: string): SnapshotFrom<any> {
	// Unknown / legacy variants (e.g. drafts persisted under a removed experiment) fall back to control.
	const flow = FLOW[draft?.metadata?.variant ?? 'control'] ?? 'control';

	return {
		status: 'active',
		value: { flow },
		context: { id: flow, draft },
		children: {
			[`${flow}ActorId`]: {
				snapshot: {
					status: 'active',
					value: step,
					context: { id: flow, draft },
					children: {},
				},
				src: `${flow}ActorSrc`,
			},
		},
	} satisfies RequiredSnapshot;
}
